import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';
import * as moment from 'moment';
import { Form } from 'antd';
import { Radio } from '../../atoms/Radio/Radio';
import { Checkbox } from '../../atoms/Checkbox/Checkbox';
import { Slider } from '../../atoms/Slider/Slider';
import {
  filterFormItemLayout,
  filterTailLayout,
  defaultTokenSeperators,
  sanitiseObject,
} from './utils';
import { Button } from '../../atoms/Button/Button';
import { Input } from '../../atoms/Input/Input';
import DatePicker from '../../atoms/DatePicker/DatePicker';
import InputNumber from '../../atoms/InputNumber/InputNumber';
import TagsInput from '../TagsInput/TagsInput';
import Select from '../../atoms/Select/Select';
import TimePicker from '../../atoms/TimePicker/TimePicker';
import MultiSelectSearchFilter from 'src/components/pages/GenericConsignment/CustomFilters/MultiSelectSearchFilter';
import { useTranslation } from 'react-i18next';

const Option = Select.Option;

const useStyles = createUseStyles((theme: ThemeType) => ({
  mainDiv: {
    background: theme.color.semantic.background.neutral.secondary,
    position: 'absolute',
    zIndex: 10,
    width: '100%',
    minWidth: '500px',
    padding: theme.spacing[4],
    display: 'flex',
    flexDirection: 'column',
    boxShadow: theme.shadow.md,
    border: `${theme.border.width.sm} solid ${theme.color.semantic.border.subtle}`,
    maxWidth: '800px',
    '& .ant-calendar-picker-input': {
      padding: 0,
    },
    '& .ant-form-item': {
      marginBottom: theme.spacing[2.5],
    },
    '& .ant-legacy-form-item': {
      marginBottom: theme.spacing[2.5],
    },
    '& .ant-select-dropdown': {
      zIndex: 1050,
    },
  },
  formContainer: {
    '& .ant-form-item': {
      maxWidth: '100%',
      marginBottom: theme.spacing[2],
    },
    '& label': {
      fontSize: theme.typography.fontSize.sm,
      color: theme.color.semantic.text.secondary,
    },
    '& .ant-form-item-explain': {
      fontSize: theme.typography.fontSize.sm,
      width: '150px',
    },
    '& .ant-form-item-label': {
      textAlign: 'left',
      '& label': {
        color: theme.color.semantic.text.secondary,
      },
    },
    '& .ant-select, .ant-input, .ant-picker, .ant-picker-range': {
      minWidth: '270px',
      width: 'auto',
    },
  },
  selectStyle: {
    minWidth: '270px',
  },
  inputNumber: {
    minWidth: '270px',
    width: 'auto',
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: '25px',
  },
  clearAllButton: {
    fontSize: `${theme.typography.fontSize.xs} !important`,
    fontWeight: `${theme.typography.fontWeight.medium} !important`,
    color: `${theme.color.semantic.text.secondary} !important`,
    padding: `${theme.spacing[0]} !important`,
    height: 'auto !important',
    '&:hover': {
      color: `${theme.color.semantic.text.secondary} !important`,
    },
    '&[disabled]': {
      color: `${theme.color.semantic.text.secondary} !important`,
    },
  },
  clearAllButtonActive: {
    fontSize: `${theme.typography.fontSize.xs} !important`,
    fontWeight: `${theme.typography.fontWeight.medium} !important`,
    color: `${theme.color.semantic.brand.danger.primary} !important`,
    padding: `${theme.spacing[0]} !important`,
    height: 'auto !important',
    '&:hover': {
      color: `${theme.color.semantic.brand.danger.primary} !important`,
    },
  },
}));

interface FilterPopupProps {
  onFilterClose: () => void;
  filtersToShow: any[];
  existingFilters: any[];
  setCurrentCustomFilters: (filters: any[]) => void;
  selectedFilters: any;
  applyFilters: (filters: any) => void;
  showCustomColumnsOption?: boolean;
  handleCustomColumnsOptionClick?: () => void;
  resetFilters?: () => void;
}

const FilterPopup: React.FC<FilterPopupProps> = (props) => {
  const {
    onFilterClose,
    filtersToShow,
    existingFilters,
    setCurrentCustomFilters,
    selectedFilters,
    applyFilters,
    showCustomColumnsOption,
    handleCustomColumnsOptionClick,
    resetFilters,
  } = props;
  const [searchFilterValues, setSearchFilterValues] = React.useState({});
  const [isFilterAdded, setIsFilterAdded] = React.useState(false);

  const [form] = Form.useForm();
  const { t } = useTranslation();
  const classes = useStyles();

  React.useEffect(() => {
    form.setFieldsValue(selectedFilters);
  }, [selectedFilters]);

  React.useEffect(() => {
    setIsFilterAdded(hasSelectedFilters());
  }, [searchFilterValues]);

  const onFinish = (values) => {
    const appliedFilterValues = sanitiseObject({
      ...selectedFilters,
      ...values,
      ...searchFilterValues,
    });
    applyFilters(appliedFilterValues);

    if (setCurrentCustomFilters) {
      const appliedFilterIds = Object.keys(appliedFilterValues);
      const customFilters = existingFilters.map((ele) => {
        return {
          ...ele,
          show_on_main_dashboard: !ele.show_on_main_dashboard
            ? appliedFilterIds.includes(ele.id)
            : ele.show_on_main_dashboard,
        };
      });
      setCurrentCustomFilters(customFilters);
    }

    onFilterClose();
  };

  const handleClearAllFilters = () => {
    if (resetFilters && isFilterAdded) {
      resetFilters();
      onFilterClose();
    }
  };

  const hasSelectedFilters = () => {
    const currentValues = form.getFieldsValue();
    const combinedValues = { ...selectedFilters, ...currentValues, ...searchFilterValues };
    return Object.keys(combinedValues).some(key => {
      const value = combinedValues[key];
      return value !== undefined && value !== null && value !== '' && 
             (Array.isArray(value) ? value.length > 0 : true);
    });
  };

  const generateOptionsFromList = (list) => {
    const optionsList: any = [];
    if (list) {
      list.forEach((element) => {
        optionsList.push(
          <Option key={element.value} value={element.value}>
            {element.label}
          </Option>,
        );
      });
    }
    return optionsList;
  };

  const renderMultipleInput = (filter) => {
    let tokenSeparators = new Set([
      ...defaultTokenSeperators,
      ...(filter.tokenSeparators || []),
    ]);

    if(filter.ignoreDefaultSeparators){
      tokenSeparators = filter.tokenSeparators || [];
    }

    return (
      <TagsInput
        notFoundContent={''}
        className={classes.selectStyle}
        placeholder={filter.placeholder || ''}
        tokenSeparators={Array.from(tokenSeparators)}
        maxHeight={100}
      />
    );
  };

  const renderSingleInput = (filter) => {
    return (
      <Input
        placeholder={filter.placeholder || ''}
        className={classes.selectStyle}
      />
    );
  };

  const renderSelect = (filter) => {
    if(filter.type === 'select' && Array.isArray(filter.options) && filter.options.length > 0) {
      filter.options.forEach((i) => {
        i.label = t(i.label);
      });
    }
    return (
      <Select
        allowClear
        showSearch
        mode={filter.mode === 'multiple' ? 'multiple' : undefined}
        placeholder={filter.placeholder || ''}
        className={classes.selectStyle}
        optionFilterProp="children"
        getPopupContainer={() => document.body}
        filterOption={(input, option: any) =>
          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {generateOptionsFromList(filter.options)}
      </Select>
    );
  };

  const handleSearchChange = (filterId, value) => {
    setSearchFilterValues({
      ...searchFilterValues,
      [filterId]: value,
    });
  };

  const renderSlider = (filter) => {
    return (
      <Slider
        style={{ width: '60%' }}
        range
        defaultValue={[
          (filter.defaultMinValue as number) ?? 0,
          (filter.defaultMaxValue as number) ?? 0,
        ]}
        min={filter.minValue || 0}
        max={filter.maxValue || 1}
      />
    );
  };

  const renderSearch = (filter) => {
    const isSingleSelect = filter.mode !== 'multiple' || filter.type === 'radio';
    return (
      <MultiSelectSearchFilter
        filter={filter}
        applyFilters={handleSearchChange}
        selectedFilters={selectedFilters}
        isSingleSelect={isSingleSelect}
      />
    );
  };

  const renderRadio = (filter) => {
    if(Array.isArray(filter.options) && filter.options.length > 0) {
      filter.options.forEach((i) => {
        i.label = t(i.label);
      });
    }
    return <Radio.Group name={filter.id} options={filter.options} />;
  };

  const renderCheckbox = (filter) => {
    if(Array.isArray(filter.options) && filter.options.length > 0) {
      filter.options.forEach((i) => {
        i.label = t(i.label);
      });
    }
    return <Checkbox.Group name={filter.id} options={filter.options} />;
  };

  const getShowTime = (showTime, timeFormat) => {
    return showTime ? { format: timeFormat || 'HH:mm:ss' } : false;
  };

  const renderDate = (filter) => {
    if (filter.mode === 'range') {
      return (
        <DatePicker.RangePicker
          className={classes.selectStyle}
          format={filter.date_format || 'YYYY-MM-DD'}
          ranges={{
            Today: [moment(), moment()],
            'Last 7 days': [moment().subtract(6, 'day'), moment()],
            'Last 30 days': [moment().subtract(29, 'day'), moment()],
            'This Month': [moment().startOf('month'), moment().endOf('month')],
          }}
          showTime={getShowTime(filter.show_time, filter.time_format)}
        />
      );
    }
    return (
      <DatePicker
        className={classes.selectStyle}
        format={filter.date_format || 'YYYY-MM-DD'}
        showTime={getShowTime(filter.show_time, filter.time_format)}
      />
    );
  };

  const renderTime = (filter) => {
    if (filter.mode === 'range') {
      return (
        <TimePicker.RangePicker
          className={classes.selectStyle}
          format={filter.time_format || 'HH:mm:ss'}
        />
      );
    }
    return (
      <TimePicker
        format={filter.time_format || 'HH:mm:ss'}
        className={classes.selectStyle}
      />
    );
  };

  const renderInputNumber = (filter) => {
    return (
      <InputNumber
        placeholder={filter.placeholder || ''}
        className={classes.inputNumber}
      />
    );
  };

  const renderFilter = (filter) => {
    if (typeof filter.type !== 'string') {
      return null;
    }

    switch (filter.type.toLowerCase()) {
      case 'input':
        return filter.mode === 'multiple'
          ? renderMultipleInput(filter)
          : renderSingleInput(filter);
      case 'select':
        return renderSelect(filter);
      case 'search':
        return renderSearch(filter);
      case 'radio':
        return renderRadio(filter);
      case 'checkbox':
        return renderCheckbox(filter);
      case 'date':
      case 'datetime':
        return renderDate(filter);
      case 'time':
        return renderTime(filter);
      case 'slider_range':
        return renderSlider(filter);
      case 'inputnumber':
        return renderInputNumber(filter);
      default:
        return null;
    }
  };

  const getInitialValues = () => {
    const defaultValues = {};
    filtersToShow.forEach((ele) => {
      defaultValues[ele.id] = ele.default;
    });
    return defaultValues;
  };

  const getRule = (validationKey, validationObj) => {
    switch (validationKey) {
      case 'min':
        return { min: validationObj.value, message: validationObj.message };
      case 'max':
        return { max: validationObj.value, message: validationObj.message };
      case 'regex':
        return {
          pattern: new RegExp(validationObj.value),
          message: validationObj.message,
        };
      case 'type':
        return { type: validationObj.value, message: validationObj.message };
      default:
        return undefined;
    }
  };

  const getRules = (filter) => {
    let validationsObj = filter.validation;
    const rules: any = [];
    if (
      validationsObj instanceof Object &&
      Object.keys(validationsObj).length
    ) {
      if (validationsObj.input_length instanceof Object) {
        validationsObj = {
          ...validationsObj,
          ...validationsObj.input_length,
        };
        delete validationsObj['input_length'];
      }
      const validationKeys = Object.keys(validationsObj);
      validationKeys.forEach((ele) => {
        const rule = getRule(ele, validationsObj[ele]);
        rule && rules.push(rule);
      });
      return rules;
    }
    return [];
  };

  return (
    <div className={classes.mainDiv}>
      <Form
        onFinish={onFinish}
        onValuesChange={() => {
          setIsFilterAdded(hasSelectedFilters());
        }}
        {...filterFormItemLayout}
        className={classes.formContainer}
        form={form}
        initialValues={getInitialValues()}
      >
        <Form.Item {...filterTailLayout}>
          <div className={classes.actionButtons}>
            <div>
              <Button
                type="primary"
                htmlType="submit"
                style={{ margin: '0px 10px' }}
              >
                {t('apply_filter')}
              </Button>
              <Button onClick={onFilterClose} type="secondary" danger>
                {t('cancel')}
              </Button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {resetFilters && (
                <Button
                  type="link"
                  onClick={handleClearAllFilters}
                  className={isFilterAdded ? classes.clearAllButtonActive : classes.clearAllButton}
                  disabled={!isFilterAdded}
                >
                  {t('Clear All Filters')}
                </Button>
              )}
              {showCustomColumnsOption && (
                <Button
                  type="link"
                  onClick={() => handleCustomColumnsOptionClick()}
                >
                  {t('customise_filters')}
                </Button>
              )}
            </div>
          </div>
        </Form.Item>
        <div style={{ maxHeight: '260px', overflow: 'auto' }}>
          {filtersToShow.map((filter) => {
            return (
              <Form.Item
                label={filter.label}
                name={filter.id}
                rules={getRules(filter)}
                colon={false}
                labelCol={{ span: 8 }}
                key={filter.id}
              >
                {renderFilter(filter)}
              </Form.Item>
            );
          })}
        </div>
      </Form>
    </div>
  );
};

export default FilterPopup;

